import React, { useState, useRef, useEffect } from "react";
import { css } from "@emotion/react";

export interface JWTooltipProps {
  children: React.ReactNode;
  title: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  maxWidth?: number;
  className?: string;
}

const JWTooltip: React.FC<JWTooltipProps> = ({
  children,
  title,
  placement = "top",
  maxWidth = 300,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = rect.top - (tooltipRect?.height || 0) - 8;
        left = rect.left + rect.width / 2 - (tooltipRect?.width || 0) / 2;
        break;
      case "bottom":
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2 - (tooltipRect?.width || 0) / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2 - (tooltipRect?.height || 0) / 2;
        left = rect.left - (tooltipRect?.width || 0) - 8;
        break;
      case "right":
        top = rect.top + rect.height / 2 - (tooltipRect?.height || 0) / 2;
        left = rect.right + 8;
        break;
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 8) left = 8;
    if (left + (tooltipRect?.width || 0) > viewportWidth - 8) {
      left = viewportWidth - (tooltipRect?.width || 0) - 8;
    }
    if (top < 8) top = 8;
    if (top + (tooltipRect?.height || 0) > viewportHeight - 8) {
      top = viewportHeight - (tooltipRect?.height || 0) - 8;
    }

    setPosition({ top, left });
    setIsVisible(true);
  };

  const hideTooltip = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isVisible) {
        hideTooltip();
      }
    };

    const handleResize = () => {
      if (isVisible) {
        hideTooltip();
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isVisible]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        onTouchStart={showTooltip}
        onTouchEnd={hideTooltip}
        css={css`
          display: inline-flex;
          align-items: center;
          cursor: pointer;
        `}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={className}
          css={css`
            position: fixed;
            top: ${position.top}px;
            left: ${position.left}px;
            z-index: 1000;
            max-width: ${maxWidth}px;
            padding: 12px 16px;
            background: white;
            border: 1px solid #daf2ff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            font-size: 13px;
            font-weight: 400;
            color: #243b4a;
            line-height: 1.4;
            white-space: normal;
            word-wrap: break-word;
            pointer-events: none;

            /* Ensure tooltip is visible on mobile */
            @media (max-width: 768px) {
              max-width: calc(100vw - 16px);
              left: 8px !important;
              right: 8px;
              width: auto;
            }
          `}
        >
          {title}
        </div>
      )}
    </>
  );
};

export default JWTooltip;
